import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "./firebase";
import { doc, getDoc, updateDoc, increment } from "firebase/firestore";
import {
  Activity,
  ArrowLeft,
  Calendar,
  Download,
  FileText,
  Eye,
  Share2,
  User,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react";
import ResearchSeo from "./ResearchSeo";

const isImageUrl = (url = "") =>
  /\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/i.test((url || "").toString());
const isPdfUrl = (url = "") => /\.pdf(\?.*)?$/i.test((url || "").toString());

const ResearchDetail = () => {
  const { doctorId, researchId } = useParams();
  const navigate = useNavigate();

  const [research, setResearch] = useState(null);
  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showImageModal, setShowImageModal] = useState(false);

  const styles = {
    container: {
      minHeight: "100vh",
      background: "#F3F4F6",
      fontFamily:
        "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
    },
    header: {
      position: "sticky",
      top: 0,
      zIndex: 40,
      backgroundColor: "rgba(243,244,246,0.95)",
      backdropFilter: "blur(18px)",
      borderBottom: "1px solid rgba(229,231,235,0.7)",
    },
    headerContent: {
      maxWidth: 480,
      margin: "0 auto",
      padding: "10px 14px",
      display: "flex",
      alignItems: "center",
      gap: 10,
    },
    backButton: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "6px 10px",
      backgroundColor: "white",
      color: "#111827",
      border: "1px solid #E5E7EB",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 600,
      cursor: "pointer",
      boxShadow: "0 1px 4px rgba(15,23,42,0.06)",
    },
    logoContainer: {
      width: 30,
      height: 30,
      borderRadius: 999,
      background: "linear-gradient(135deg,#3b82f6,#6366f1)",
      display: "grid",
      placeItems: "center",
      boxShadow: "0 4px 12px rgba(59,130,246,0.4)",
    },
    headerTitleWrap: {
      display: "flex",
      flexDirection: "column",
      gap: 2,
    },
    headerTitle: {
      margin: 0,
      fontSize: 14,
      fontWeight: 700,
      color: "#0F172A",
    },
    headerSub: {
      margin: 0,
      fontSize: 11,
      color: "#6B7280",
    },
    mainContent: {
      maxWidth: 480,
      margin: "0 auto",
      padding: "14px 14px 24px",
    },
    researchCard: {
      backgroundColor: "#FFFFFF",
      borderRadius: 18,
      border: "1px solid rgba(229,231,235,0.9)",
      boxShadow: "0 10px 30px rgba(15,23,42,0.10)",
      overflow: "hidden",
    },
    // Top hero image (if exists)
    heroImageWrap: {
      width: "100%",
      maxHeight: 260,
      overflow: "hidden",
      position: "relative",
      background: "#0F172A",
      cursor: "pointer",
    },
    heroImage: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      display: "block",
      transform: "scale(1.02)",
    },
    heroBadge: {
      position: "absolute",
      left: 10,
      bottom: 10,
      padding: "4px 10px",
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 600,
      background: "rgba(15,23,42,0.8)",
      color: "white",
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
    },
    researchHeader: {
      padding: "14px 14px 10px",
    },
    researchTitle: {
      margin: "0 0 6px 0",
      fontSize: 18,
      lineHeight: 1.3,
      fontWeight: 700,
      color: "#111827",
    },
    researchMeta: {
      display: "flex",
      flexWrap: "wrap",
      gap: 8,
      alignItems: "center",
      fontSize: 11.5,
      color: "#6b7280",
      marginBottom: 10,
    },
    metaItem: {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      padding: "4px 8px",
      borderRadius: 999,
      backgroundColor: "#F3F4F6",
    },
    authorInfo: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "10px 10px",
      backgroundColor: "#F9FAFB",
      borderRadius: 14,
      border: "1px solid #E5E7EB",
      marginTop: 4,
    },
    authorAvatar: {
      width: 42,
      height: 42,
      borderRadius: "50%",
      objectFit: "cover",
      border: "2px solid #fff",
      boxShadow: "0 2px 8px rgba(15,23,42,0.18)",
    },
    authorAvatarFallback: {
      width: 42,
      height: 42,
      borderRadius: "50%",
      display: "grid",
      placeItems: "center",
      background: "linear-gradient(135deg,#3b82f6,#6366f1)",
      border: "2px solid #fff",
      color: "#fff",
      boxShadow: "0 2px 8px rgba(15,23,42,0.18)",
    },
    authorName: { margin: 0, fontSize: 14, fontWeight: 700, color: "#111827" },
    authorSpecialty: { margin: 0, fontSize: 11.5, color: "#6b7280" },
    viewProfileButton: {
      marginLeft: "auto",
      padding: "6px 10px",
      borderRadius: 999,
      border: "1px solid rgba(148,163,184,0.6)",
      backgroundColor: "#FFFFFF",
      color: "#0F172A",
      fontSize: 11.5,
      fontWeight: 600,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
    },
    contentSection: {
      padding: "10px 14px 14px",
      display: "flex",
      flexDirection: "column",
      gap: 12,
    },
    description: {
      fontSize: 13.5,
      lineHeight: 1.7,
      color: "#374151",
      whiteSpace: "pre-wrap",
    },
    sectionTitle: {
      margin: "0 0 6px 0",
      fontSize: 13,
      fontWeight: 700,
      color: "#111827",
    },
    mediaGrid: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 8,
    },
    mediaItem: {
      position: "relative",
      aspectRatio: "4/3",
      borderRadius: 12,
      overflow: "hidden",
      cursor: "pointer",
      border: "1px solid #E5E7EB",
      background: "#F9FAFB",
    },
    mediaImage: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      display: "block",
    },
    mediaPdf: {
      width: "100%",
      height: "100%",
      background:
        "radial-gradient(circle at top, #f97316 0, #b91c1c 45%, #7f1d1d 100%)",
      display: "grid",
      placeItems: "center",
      color: "white",
    },
    mediaOverlay: {
      position: "absolute",
      inset: 0,
      background:
        "linear-gradient(to top, rgba(15,23,42,0.80), rgba(15,23,42,0))",
      display: "flex",
      alignItems: "flex-end",
      padding: 8,
      opacity: 0,
      transition: "opacity .15s ease",
    },
    mediaLabel: { color: "#fff", fontSize: 11.5, fontWeight: 600 },
    actionsRow: {
      display: "flex",
      flexWrap: "wrap",
      gap: 8,
      marginTop: 4,
    },
    pillButton: {
      flex: "1 1 45%",
      minWidth: "45%",
      padding: "8px 10px",
      borderRadius: 999,
      border: "1px solid #E5E7EB",
      backgroundColor: "#FFFFFF",
      fontSize: 12,
      fontWeight: 600,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      cursor: "pointer",
    },
    pillPrimary: {
      background:
        "linear-gradient(135deg, #3b82f6 0%, #6366f1 50%, #8b5cf6 100%)",
      border: "none",
      color: "#FFFFFF",
      boxShadow: "0 10px 18px -14px rgba(79,70,229,0.5)",
    },
    statsRow: {
      display: "flex",
      justifyContent: "space-between",
      gap: 8,
      marginTop: 8,
    },
    statBox: {
      flex: 1,
      padding: "8px 6px",
      borderRadius: 12,
      backgroundColor: "#F9FAFB",
      border: "1px solid #E5E7EB",
      textAlign: "center",
    },
    statNumber: {
      margin: 0,
      fontSize: 16,
      fontWeight: 700,
      color: "#111827",
    },
    statLabel: {
      margin: 0,
      fontSize: 11,
      fontWeight: 600,
      color: "#6B7280",
    },
    loadingWrap: {
      minHeight: "100vh",
      display: "grid",
      placeItems: "center",
      gap: 12,
      color: "#6b7280",
      fontWeight: 600,
    },
    spinner: {
      width: 40,
      height: 40,
      border: "3px solid #E5E7EB",
      borderTop: "3px solid #3b82f6",
      borderRadius: "50%",
      animation: "spin 1s linear infinite",
    },
    errorWrap: {
      minHeight: "100vh",
      display: "grid",
      placeItems: "center",
      padding: 20,
    },
    errorCard: {
      background: "#fff",
      borderRadius: 16,
      border: "1px solid #E5E7EB",
      boxShadow: "0 18px 40px -28px rgba(15,23,42,0.4)",
      padding: 20,
      textAlign: "center",
      maxWidth: 420,
      width: "100%",
    },
    modal: {
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,.9)",
      display: "grid",
      placeItems: "center",
      zIndex: 1000,
      padding: 16,
    },
    modalContent: {
      position: "relative",
      maxWidth: "92vw",
      maxHeight: "92vh",
      background: "#000",
      borderRadius: 12,
      overflow: "hidden",
    },
    modalImage: {
      width: "100%",
      height: "100%",
      objectFit: "contain",
      background: "#000",
    },
    modalControls: {
      position: "absolute",
      top: 10,
      right: 10,
      display: "flex",
      gap: 8,
      zIndex: 2,
    },
    modalButton: {
      width: 36,
      height: 36,
      borderRadius: "50%",
      background: "rgba(0,0,0,.75)",
      color: "#fff",
      border: "none",
      cursor: "pointer",
      display: "grid",
      placeItems: "center",
    },
    navBtn: {
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      width: 40,
      height: 40,
      borderRadius: "50%",
      background: "rgba(0,0,0,.75)",
      color: "#fff",
      border: "none",
      cursor: "pointer",
      display: "grid",
      placeItems: "center",
      zIndex: 2,
    },
    navPrev: { left: 10 },
    navNext: { right: 10 },
  };

  // Fetch research + doctor
  useEffect(() => {
    const run = async () => {
      try {
        const researchRef = doc(db, "uploads", doctorId, "items", researchId);
        const snap = await getDoc(researchRef);
        if (!snap.exists()) {
          setError("Research not found");
          setLoading(false);
          return;
        }
        const data = snap.data();
        const createdAt =
          data.createdAt?.toDate?.() ??
          (typeof data.createdAt === "string"
            ? new Date(data.createdAt)
            : new Date());
        setResearch({ id: snap.id, ...data, createdAt, doctorId });

        // best-effort view increment
        try {
          await updateDoc(researchRef, { views: increment(1) });
          setResearch((p) =>
            p ? { ...p, views: (p.views || 0) + 1 } : p
          );
        } catch {}

        // fetch doctor
        try {
          const doctorRef = doc(db, "doctors", doctorId);
          const dsnap = await getDoc(doctorRef);
          if (dsnap.exists()) setDoctor(dsnap.data());
        } catch {}
      } catch (e) {
        setError("Failed to load research");
      } finally {
        setLoading(false);
      }
    };
    if (doctorId && researchId) run();
  }, [doctorId, researchId]);

  const files = useMemo(
    () => (Array.isArray(research?.files) ? research.files : []),
    [research]
  );
  const imageFiles = useMemo(
    () => files.filter(isImageUrl),
    [files]
  );
  const pdfFiles = useMemo(
    () => files.filter(isPdfUrl),
    [files]
  );

  const canonicalUrl = useMemo(
    () => `${window.location.origin}/research/${doctorId}/${researchId}`,
    [doctorId, researchId]
  );

  const handleDownload = useCallback(
    async (fileUrl) => {
      const researchRef = doc(db, "uploads", doctorId, "items", researchId);
      try {
        await updateDoc(researchRef, { downloads: increment(1) });
        setResearch((p) =>
          p ? { ...p, downloads: (p.downloads || 0) + 1 } : p
        );
      } catch {}
      window.open(fileUrl, "_blank", "noopener,noreferrer");
    },
    [doctorId, researchId]
  );

  const handleShare = useCallback(async () => {
    const shareData = {
      title: research?.title || "MikDok Research",
      text: research?.description || "",
      url: canonicalUrl,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareData.url);
        alert("Link u kopjua!");
      }
    } catch {
      try {
        await navigator.clipboard.writeText(shareData.url);
        alert("Link u kopjua!");
      } catch {}
    }
  }, [research, canonicalUrl]);

  // Modal handlers
  const openImageModal = (idx) => {
    if (!imageFiles.length) return;
    setCurrentImageIndex(
      Math.max(0, Math.min(idx, imageFiles.length - 1))
    );
    setShowImageModal(true);
  };
  const closeImageModal = () => setShowImageModal(false);
  const nextImage = () =>
    imageFiles.length &&
    setCurrentImageIndex((i) => (i + 1) % imageFiles.length);
  const prevImage = () =>
    imageFiles.length &&
    setCurrentImageIndex(
      (i) => (i - 1 + imageFiles.length) % imageFiles.length
    );

  useEffect(() => {
    const onKey = (e) => {
      if (!showImageModal) return;
      if (e.key === "Escape") closeImageModal();
      if (e.key === "ArrowRight") nextImage();
      if (e.key === "ArrowLeft") prevImage();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showImageModal, imageFiles.length]);

  if (loading) {
    return (
      <div style={styles.container}>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <div style={styles.loadingWrap}>
          <div style={styles.spinner} />
          <div>Duke ngarkuar…</div>
        </div>
      </div>
    );
  }

  if (error || !research) {
    return (
      <div style={styles.container}>
        <div style={styles.errorWrap}>
          <div style={styles.errorCard}>
            <FileText
              style={{
                width: 44,
                height: 44,
                color: "#ef4444",
                marginBottom: 10,
              }}
            />
            <h2
              style={{
                margin: "0 0 6px 0",
                fontSize: 18,
                fontWeight: 800,
                color: "#111827",
              }}
            >
              {error || "Research nuk u gjet"}
            </h2>
            <p
              style={{
                color: "#6b7280",
                margin: "0 0 14px 0",
                fontWeight: 500,
                fontSize: 13,
              }}
            >
              Përmbajtja që kërkon mund të jetë fshirë ose nuk ekziston më.
            </p>
            <button
              style={{ ...styles.backButton, marginTop: 4 }}
              onClick={() => navigate(-1)}
            >
              <ArrowLeft size={16} />
              Kthehu mbrapa
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* SEO tags */}
      <ResearchSeo
        research={research}
        doctor={doctor}
        canonicalUrl={canonicalUrl}
      />

      <style>{`
        .media-item:hover .media-overlay { opacity: 1; }
        @media (max-width: 480px){
          .media-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <button
            type="button"
            onClick={() => navigate(-1)}
            style={styles.backButton}
          >
            <ArrowLeft size={16} />
            Mbrapa
          </button>
          <div style={styles.logoContainer}>
            <Activity
              style={{ color: "#fff", width: 16, height: 16 }}
            />
          </div>
          <div style={styles.headerTitleWrap}>
            <p style={styles.headerTitle}>Research</p>
            <p style={styles.headerSub}>Postim nga mjeku në MikDok</p>
          </div>
        </div>
      </div>

      <div style={styles.mainContent}>
        <section style={styles.researchCard}>
          {/* Hero image if exists */}
          {imageFiles.length > 0 && (
            <div
              style={styles.heroImageWrap}
              onClick={() => openImageModal(0)}
            >
              <img
                src={imageFiles[0]}
                alt=""
                style={styles.heroImage}
              />
              <div style={styles.heroBadge}>
                <Eye size={12} />
                {research.views || 0} shikime
              </div>
            </div>
          )}

          {/* Title + meta + author */}
          <header style={styles.researchHeader}>
            <h1 style={styles.researchTitle}>{research.title}</h1>

            <div style={styles.researchMeta}>
              <span style={styles.metaItem}>
                <Calendar size={12} />
                {research.createdAt &&
                  research.createdAt.toLocaleDateString("sq-AL", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
              </span>
              <span style={styles.metaItem}>
                <Eye size={12} />
                {research.views || 0} views
              </span>
              <span style={styles.metaItem}>
                <Download size={12} />
                {research.downloads || 0} shkarkime
              </span>
              <span style={styles.metaItem}>
                <FileText size={12} />
                {files.length} file
              </span>
            </div>

            {doctor && (
              <div style={styles.authorInfo}>
                {doctor.photoURL ? (
                  <img
                    src={doctor.photoURL}
                    alt={doctor.name}
                    style={styles.authorAvatar}
                  />
                ) : (
                  <div style={styles.authorAvatarFallback}>
                    <User size={18} />
                  </div>
                )}
                <div>
                  <p style={styles.authorName}>
                    {doctor.name || "Mjek"}
                  </p>
                  {!!doctor.specialty && (
                    <p style={styles.authorSpecialty}>
                      {doctor.specialty}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => navigate(`/doctor/${doctorId}`)}
                  style={styles.viewProfileButton}
                >
                  <User size={13} />
                  Profili
                </button>
              </div>
            )}
          </header>

          {/* Content */}
          <div style={styles.contentSection}>
            {!!research.description && (
              <div>
                <p style={styles.description}>{research.description}</p>
              </div>
            )}

            {(imageFiles.length > 1 || pdfFiles.length > 0) && (
              <div>
                <h3 style={styles.sectionTitle}>Skedarët e research-it</h3>
                <div
                  style={styles.mediaGrid}
                  className="media-grid"
                >
                  {/* Extra images (skip first hero) */}
                  {imageFiles.slice(1).map((file, idx) => (
                    <div
                      key={`img-${idx + 1}`}
                      style={styles.mediaItem}
                      className="media-item"
                      onClick={() => openImageModal(idx + 1)}
                    >
                      <img
                        src={file}
                        alt=""
                        style={styles.mediaImage}
                      />
                      <div
                        style={styles.mediaOverlay}
                        className="media-overlay"
                      >
                        <span style={styles.mediaLabel}>
                          Image {idx + 2}
                        </span>
                      </div>
                    </div>
                  ))}

                  {pdfFiles.map((file, idx) => (
                    <div
                      key={`pdf-${idx}`}
                      style={styles.mediaItem}
                      className="media-item"
                      onClick={() => handleDownload(file)}
                    >
                      <div style={styles.mediaPdf}>
                        <FileText style={{ width: 32, height: 32 }} />
                      </div>
                      <div
                        style={styles.mediaOverlay}
                        className="media-overlay"
                      >
                        <span style={styles.mediaLabel}>
                          PDF Document
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions row (Insta-style pill buttons) */}
            <div>
              <h3 style={styles.sectionTitle}>Veprime</h3>
              <div style={styles.actionsRow}>
                {pdfFiles.length > 0 && (
                  <button
                    type="button"
                    style={{ ...styles.pillButton, ...styles.pillPrimary }}
                    onClick={() => handleDownload(pdfFiles[0])}
                  >
                    <Download size={14} />
                    Shkarko PDF
                  </button>
                )}

                <button
                  type="button"
                  style={styles.pillButton}
                  onClick={handleShare}
                >
                  <Share2 size={14} />
                  Ndaj me të tjerët
                </button>

                <button
                  type="button"
                  style={styles.pillButton}
                  onClick={() => navigate(`/doctor/${doctorId}`)}
                >
                  <User size={14} />
                  Shiko mjekun
                </button>
              </div>
            </div>

            {/* Stats row */}
            <div style={styles.statsRow}>
              <div style={styles.statBox}>
                <p style={styles.statNumber}>
                  {research.downloads || 0}
                </p>
                <p style={styles.statLabel}>Shkarkime</p>
              </div>
              <div style={styles.statBox}>
                <p style={styles.statNumber}>{research.views || 0}</p>
                <p style={styles.statLabel}>Shikime</p>
              </div>
              <div style={styles.statBox}>
                <p style={styles.statNumber}>{files.length}</p>
                <p style={styles.statLabel}>Skedarë</p>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Image Modal */}
      {showImageModal && imageFiles.length > 0 && (
        <div
          style={styles.modal}
          onClick={closeImageModal}
          role="dialog"
          aria-modal="true"
        >
          <div
            style={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={styles.modalControls}>
              <button
                style={styles.modalButton}
                onClick={closeImageModal}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            {imageFiles.length > 1 && (
              <>
                <button
                  style={{ ...styles.navBtn, ...styles.navPrev }}
                  onClick={prevImage}
                  aria-label="Previous image"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  style={{ ...styles.navBtn, ...styles.navNext }}
                  onClick={nextImage}
                  aria-label="Next image"
                >
                  <ChevronRight size={20} />
                </button>
              </>
            )}

            <img
              src={imageFiles[currentImageIndex]}
              alt=""
              style={styles.modalImage}
              draggable={false}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ResearchDetail;
